/**
 * TBB Seller Profile Header — JS hydration
 *
 * Fetches the vendor profile from the rankings CF worker
 * (rankings.throwbackbuys.com/vendor?handle=xxx) and populates the
 * skeleton seller header. The worker is the theme's ONLY data source
 * here — what feeds the worker (portal KV, upstream syncs) is owned by
 * the portal/engine surfaces and has changed over time; never assume a
 * specific upstream (e.g. PV) from this file.
 * Graceful fallback: if fetch fails, Liquid defaults remain visible
 * (collection.title, collection.description).
 *
 * No dependencies. Idempotent (data-tbb-seller-init flag).
 */
(function () {
  'use strict';

  // (Empty-collection class-toggle was moved to tbb-empty-collection.js
  //  — it's loaded globally from theme.liquid so it fires on every
  //  Boost grid page, not just seller-profile pages.)

  /**
   * Format large numbers: 1400 → "1.4K", 123456 → "123K"
   * @param {number} n
   * @returns {string}
   */
  function formatCount(n) {
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  }

  /**
   * Build a Cloudinary crop URL for seller avatar.
   * @param {string} url — raw Cloudinary URL from PV API
   * @returns {string}
   */
  function avatarUrl(url) {
    if (!url) return '';
    // Insert smart crop transform before /upload/ or /v1/
    return url.replace(
      /(\/upload\/|\/v\d+\/)/,
      '$1w_192,h_192,c_fill,g_face/'
    );
  }

  /**
   * Privacy guard for location strings. The worker is SUPPOSED to send
   * "City, ST", but some PV business addresses arrive as a full street string
   * (e.g. "261 5th st e, Apt 406, Saint Paul MN 55101, US"). We must never
   * render street / apt / zip publicly — reduce to City + State (or city +
   * region for international), and if we can't safely parse it, return empty
   * rather than leak the seller's home address.
   */
  function sanitizeLocation(str) {
    str = String(str || '').trim();
    if (!str) return '';
    var parts = str.split(',').map(function (p) {
      return p.trim().replace(/\s+\d{4,}(-\d{4})?\s*$/, '').trim(); // strip trailing ZIP
    }).filter(function (p) {
      if (!p) return false;
      if (/^\d/.test(p)) return false;                                          // street-number line
      if (/\b(apt|suite|ste|unit|floor|bldg|po\s*box)\b/i.test(p) && /\d/.test(p)) return false; // apt/suite line
      if (/#\s*\w/.test(p)) return false;                                        // "# 4"
      if (/^\d+$/.test(p)) return false;                                         // bare number
      return true;
    });
    // Drop a trailing country token so we keep City + State, not City, State, US.
    if (parts.length >= 2 && /^(us|u\.?s\.?a?\.?|usa|united states|canada)$/i.test(parts[parts.length - 1])) {
      parts = parts.slice(0, -1);
    }
    var tail = parts.length ? parts[parts.length - 1] : '';
    var m = tail.match(/^(.+?)\s+([A-Za-z]{2})$/);          // "Saint Paul MN" -> City + ST
    var out;
    if (m) out = m[1].trim() + ', ' + m[2].toUpperCase();
    else if (parts.length >= 2) out = parts[parts.length - 2] + ', ' + tail; // City, Region
    else out = tail;
    return /\d{3,}/.test(out) ? '' : out;                   // backstop: if a street #/zip survived, render nothing
  }

  /**
   * Build a Cloudinary-optimized banner URL. PV stores 1680×360 PNGs at
   * full resolution (typically 200–500KB). Cloudinary on-the-fly transforms
   * cut weight 60–80% via WebP/AVIF + adaptive quality while keeping native
   * resolution so the banner doesn't get browser-upscaled on wide displays:
   *   - f_auto      serves WebP/AVIF when supported, falls back to PNG/JPG
   *   - q_auto:eco  aggressive auto-quality, small visual diff for big size win
   *   - w_1680      match PV's native upload spec (1680×360). Earlier w_900
   *                 cap was causing 70% browser upscale on desktop and a
   *                 visibly soft banner. Mobile clients still get the auto
   *                 quality + format savings; the weight win is large enough
   *                 there that we don't need a smaller width variant.
   * Returns the original URL unchanged if not Cloudinary (defensive).
   * @param {string} url — raw Cloudinary URL from PV API
   * @returns {string}
   */
  function bannerUrl(url) {
    if (!url) return '';
    if (url.indexOf('res.cloudinary.com') === -1) return url;
    return url.replace(
      /(\/upload\/)(?!f_auto)/,
      '$1f_auto,q_auto:eco,w_1680/'
    );
  }

  /**
   * Render the vendor status pill from a status-card API response.
   * Format: `{emoji} {text}` — emoji optional. Text-only is fine.
   * Uses textContent to keep API data XSS-safe.
   */
  function renderStatusPill(pillEl, status) {
    if (!pillEl || !status || !status.text) return;
    // Reset (in case of re-hydration)
    pillEl.textContent = '';
    if (status.emoji) {
      var emojiEl = document.createElement('span');
      emojiEl.className = 'tbb-vendor-status-pill-emoji';
      emojiEl.textContent = status.emoji;
      emojiEl.setAttribute('aria-hidden', 'true');
      pillEl.appendChild(emojiEl);
    }
    var textEl = document.createElement('span');
    textEl.className = 'tbb-vendor-status-pill-text';
    textEl.textContent = status.text;
    pillEl.appendChild(textEl);
    pillEl.removeAttribute('hidden');
  }

  /**
   * Render the vacation banner. Message priority:
   *   1. vacation.message (custom seller copy)
   *   2. "On vacation — back {returnsAt}" (if returnsAt set)
   *   3. "On vacation, back soon" (fallback)
   */
  function renderVacationBanner(bannerEl, vacation) {
    if (!bannerEl || !vacation || !vacation.active) return;
    var msg = vacation.message
      ? vacation.message
      : vacation.returnsAt
        ? 'On vacation — back ' + vacation.returnsAt
        : 'On vacation, back soon';
    bannerEl.textContent = '';
    var iconEl = document.createElement('span');
    iconEl.className = 'tbb-vendor-vacation-banner-icon';
    iconEl.textContent = '🏖️';
    iconEl.setAttribute('aria-hidden', 'true');
    var textEl = document.createElement('span');
    textEl.className = 'tbb-vendor-vacation-banner-text';
    textEl.textContent = msg;
    bannerEl.appendChild(iconEl);
    bannerEl.appendChild(textEl);
    bannerEl.removeAttribute('hidden');
  }

  /**
   * Fetch the status-card endpoint — TBB-portal-owned freeform status
   * pill ONLY. Vacation moved to PV via the marketplace worker (see
   * 2026-05-07 architecture change). Public, CORS-enabled, no auth.
   * Visual parity with the seller portal's own VendorStatusPill.
   */
  function fetchAndRenderStatusPill(container) {
    var apiBase = container.dataset.statusCardApi;
    var handle = container.dataset.vendorHandle;
    if (!apiBase || !handle) return;
    var url = apiBase.replace(/\/+$/, '') + '/' + encodeURIComponent(handle) + '/status-card';
    // 12s timeout. Bumped from 4s on 2026-05-14 — portal-staging cold
    // hits intermittently spike above the old 4s window and aborted
    // the pill render. Pill is non-critical so a longer wait is fine.
    // Deadline via TBBFetch: raced against our own timer, so it still fires
    // even if whatever owns window.fetch on the storefront drops our abort
    // signal. See DEV_NOTES 2026-07-29.
    (window.TBBFetch || fetch)(url, { timeout: 12000 })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.status || !data.status.text) return;
        var pillEl = container.querySelector('[data-tbb-status-pill]');
        renderStatusPill(pillEl, data.status);
        // Note: do NOT read vacation from this endpoint anymore. The
        // worker's data.vacation is now the source of truth.
      })
      .catch(function () {
        clearTimeout(timeoutId);
        // Silent failure — pill stays hidden. ~10s staleness is fine.
      });
  }

  /**
   * Validate / coerce a URL value coming from PV before assigning it to
   * an anchor's href. Rejects javascript:/data:/vbscript: schemes. For
   * web URLs, prepends https:// when the seller stored a bare domain
   * like "lexilouvintageboutique.com". Returns "" when input is unsafe
   * or empty so the chip stays hidden.
   */
  function safeUrl(value, scheme) {
    if (!value || typeof value !== 'string') return '';
    var v = value.trim();
    if (!v) return '';
    if (/^\s*(javascript|data|vbscript):/i.test(v)) return '';
    if (scheme === 'mailto') {
      if (!/^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/.test(v)) return '';
      return 'mailto:' + encodeURIComponent(v);
    }
    if (scheme === 'tel') {
      var digits = v.replace(/[^\d+]/g, '');
      return digits ? 'tel:' + digits : '';
    }
    // Web URL — ensure protocol prefix
    if (!/^https?:\/\//i.test(v)) v = 'https://' + v;
    try { new URL(v); return v; } catch (e) { return ''; }
  }

  /**
   * Hydrate one contact chip — set href and reveal only when `safeHref`
   * is non-empty. When empty, chip stays hidden so the row gracefully
   * collapses around populated channels (works for any 0–7 combination).
   */
  function setContactChip(container, attr, safeHref) {
    var el = container.querySelector('[' + attr + ']');
    if (!el || !safeHref) return;
    el.href = safeHref;
    el.removeAttribute('hidden');
  }

  /**
   * Creator Time Capsule header render.
   *
   * Contract (verified live 2026-08-17):
   *   creator: { handle, name, active, shareUrl, referralUrl,
   *              profile: { name, storefrontUrl, website, bio,
   *                         status: { text, emoji },
   *                         socials: {...}, social: {...} } }
   *   capsule: { count, ... }
   *   items:   [ { ..., shareUrl } ]
   *
   * This is the ONLY source for a creator card. No rankings record, no
   * product.vendor, no seller status-card -- a creator is not the seller of
   * the throwbacks they curate, and borrowing seller identity onto a curator
   * profile is a lie the buyer cannot detect.
   *
   * Every field is optional and every field hides cleanly when blank. The
   * payload currently ships bio and all socials empty, so those simply do not
   * render; nothing here invents a value or falls back to another surface.
   */
  function initCreatorHeader(container) {
    var apiBase = container.dataset.creatorApi;
    var handle = container.dataset.vendorHandle;
    if (!apiBase || !handle) {
      container.classList.add('is-hydrated');
      return;
    }
    var url = apiBase.replace(/\/+$/, '') + '/' + encodeURIComponent(handle) + '/capsule';

    // The section fires this request inline during HTML parse and stashes the
    // promise, which starts it seconds earlier than this deferred module can.
    // Use it when it is there; otherwise fetch exactly as before. A null result
    // from the head start (non-2xx, network error) also falls back here, so a
    // failed kickoff costs one retry rather than a blank card.
    var headStart = window.__tbbCreatorFetch || null;
    var pending = headStart
      ? headStart.then(function (data) {
          if (data) return data;
          return (window.TBBFetch || fetch)(url, { timeout: 12000 })
            .then(function (res) {
              if (!res.ok) throw new Error('HTTP ' + res.status);
              return res.json();
            });
        })
      : (window.TBBFetch || fetch)(url, { timeout: 12000 })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
          });

    pending
      .then(function (data) {
        if (!data || !data.creator) return;
        renderCreatorProfile(container, data);
      })
      .catch(function (err) {
        console.warn('TBB creator capsule fetch failed:', err.message);
      })
      .then(function () {
        container.classList.add('is-hydrated');
      });
  }

  function renderCreatorProfile(container, data) {
    var creator = data.creator || {};
    var profile = creator.profile || {};

    // Publish for tbb-creator-referral.js — it prefers the per-item shareUrl
    // the API returns over rewriting the href by hand.
    window.__tbbCreatorData = data;
    document.dispatchEvent(new CustomEvent('tbb:creator-loaded'));

    // Structure and behaviour mirror the seller card field for field. Every
    // element is the SAME node the seller path fills, so the two surfaces stay
    // one component. Missing values simply leave the element in its default
    // state, exactly as they do for a seller with sparse data — nothing here
    // force-hides a slot, and nothing falls back to seller data.

    // ── Name ──
    var displayName = profile.name || creator.name || '';
    var nameEl = container.querySelector('[data-seller-name]');
    if (nameEl && displayName) nameEl.textContent = displayName;

    // ── Banner ── same shell + [hidden] reveal the seller path uses.
    var bannerSrc = profile.banner || profile.profileBanner || '';
    if (bannerSrc) {
      var bannerShell = document.querySelector('[data-tbb-seller-banner]');
      var bannerImg = document.querySelector('[data-seller-banner-img]');
      if (bannerShell && bannerImg) {
        bannerImg.src = bannerUrl(bannerSrc);
        bannerImg.alt = displayName + ' banner';
        bannerShell.removeAttribute('hidden');
      }
    }

    // ── Avatar ── built with DOM methods (never innerHTML) against untrusted
    // API data, identical to the seller path.
    var avatarSrc = profile.avatar || profile.profileImage || '';
    if (avatarSrc) {
      var avatarEl = container.querySelector('[data-seller-avatar]');
      if (avatarEl) {
        var img = document.createElement('img');
        img.src = avatarUrl(avatarSrc);
        img.alt = displayName;
        img.width = 96;
        img.height = 96;
        img.loading = 'eager';
        avatarEl.textContent = '';
        avatarEl.appendChild(img);
      }
    }

    // ── Bio ──
    var bioEl = container.querySelector('[data-seller-description]');
    var bio = (profile.bio || '').trim();
    if (bioEl && bio) bioEl.textContent = bio;

    // ── Status pill ── same renderer, creator-sourced data.
    if (profile.status && profile.status.text) {
      renderStatusPill(container.querySelector('[data-tbb-status-pill]'), profile.status);
    }

    // ── Location ── same sanitiser + reveal as the seller path. Absent from
    // the contract today, so this simply does not fire yet.
    var locStr = '';
    if (typeof profile.location === 'string') {
      locStr = sanitizeLocation(profile.location);
    } else if (profile.location && typeof profile.location === 'object') {
      var city = (profile.location.city || '').trim();
      var state = (profile.location.state || '').trim();
      var country = (profile.location.country || '').trim();
      if (city && state) locStr = city + ', ' + state;
      else if (city && country) locStr = city + ', ' + country;
      else locStr = city || state || country || '';
    }
    if (locStr) {
      var locText = container.querySelector('[data-seller-location-text]');
      if (locText) locText.textContent = locStr;
      var locWrap = container.querySelector('[data-seller-location]');
      if (locWrap) locWrap.style.display = '';
    }

    // ── Stats ── Throwbacks is the CAPSULE size, not a shop size.
    var count = data.capsule && typeof data.capsule.count === 'number' ? data.capsule.count : 0;
    var prodEl = container.querySelector('[data-seller-stat-products]');
    if (prodEl) prodEl.textContent = formatCount(count);

    // ── Socials / contact ── accept either key the payload carries.
    var social = profile.socials || profile.social || {};
    setContactChip(container, 'data-seller-contact-website',   safeUrl(profile.website || social.website, 'web'));
    setContactChip(container, 'data-seller-contact-email',     safeUrl(social.email, 'mailto'));
    setContactChip(container, 'data-seller-contact-instagram', safeUrl(social.instagram, 'web'));
    setContactChip(container, 'data-seller-contact-facebook',  safeUrl(social.facebook, 'web'));
    setContactChip(container, 'data-seller-contact-youtube',   safeUrl(social.youtube, 'web'));
    setContactChip(container, 'data-seller-contact-tiktok',    safeUrl(social.tiktok, 'web'));
    setContactChip(container, 'data-seller-contact-whatsapp',  safeUrl(social.whatsapp, 'web'));
  }

  function initSellerHeader(container) {
    if (container.dataset.tbbSellerInit) return;
    container.dataset.tbbSellerInit = '1';

    // Creator Time Capsule surface.
    //
    // Creators are NOT sellers. Per the 2026-08-17 routing/data spec: the
    // creator page renders from the portal creator contract ONLY. It must not
    // call the rankings /vendor record or the vendor status-card endpoint, and
    // seller data must never overwrite a creator profile. So this returns --
    // nothing below this line runs on a creator page.
    if (container.dataset.profileType === 'creator') {
      initCreatorHeader(container);
      return;
    }


    // Status pill (freeform "🎯 hunting for rare finds") — fires
    // alongside the main vendor fetch below, hits the TBB portal's
    // public status-card endpoint. Vacation comes from the worker now.
    fetchAndRenderStatusPill(container);

    var workerUrl = container.dataset.workerUrl;
    var handle = container.dataset.vendorHandle;
    if (!workerUrl || !handle) return;

    // Build fetch URL — append /vendor path if worker URL is base domain
    var fetchUrl = workerUrl.replace(/\/+$/, '');
    if (!fetchUrl.endsWith('/vendor')) fetchUrl += '/vendor';
    fetchUrl += '?handle=' + encodeURIComponent(handle);

    // 15s timeout to prevent hanging if worker is slow/down.
    // Bumped from 5s on 2026-05-14 — PV /v1/vendors (the worker's
    // origin call) intermittently spikes to 5+s on cold CF edge cache
    // and was aborting the entire seller profile card mid-render.
    // After the KV pre-warm cron warms the worker's KV cache, /vendor
    // typically returns in ~30ms; the longer ceiling is safety net.
    // Deadline via TBBFetch: raced against our own timer, so it still fires
    // even if whatever owns window.fetch on the storefront drops our abort
    // signal. See DEV_NOTES 2026-07-29.
    (window.TBBFetch || fetch)(fetchUrl, { timeout: 15000 })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json.success || !json.data) return;
        var d = json.data;

        // Banner image — 16:9 hero shell that the ProfileCard overlaps.
        // Hidden by default (the shell has [hidden]); we surface it only
        // when PV gave us a profileBanner URL. Sellers without a banner
        // skip this entirely and the card sits at top of page.
        if (d.profileBanner) {
          var bannerShell = document.querySelector('[data-tbb-seller-banner]');
          var bannerImg = document.querySelector('[data-seller-banner-img]');
          if (bannerShell && bannerImg) {
            // Route through bannerUrl() for Cloudinary WebP/AVIF + size optimization.
            bannerImg.src = bannerUrl(d.profileBanner);
            bannerImg.alt = (d.vendorName || '') + ' banner';
            bannerShell.removeAttribute('hidden');
          }
        }

        // Avatar — use DOM methods to prevent XSS from untrusted API data
        if (d.profileImage) {
          var avatarEl = container.querySelector('[data-seller-avatar]');
          if (avatarEl) {
            var img = document.createElement('img');
            img.src = avatarUrl(d.profileImage);
            img.alt = d.vendorName || '';
            img.width = 96;
            img.height = 96;
            img.loading = 'eager';
            avatarEl.textContent = '';
            avatarEl.appendChild(img);
          }
        }

        // Vendor name — Liquid renders product.vendor (Shopify's proper-
        // cased vendor field) as the initial value. We override with PV's
        // d.vendorName ONLY when it (a) carries proper casing (uppercase or
        // whitespace) AND (b) still resolves to THIS seller's handle. Guard
        // (b) stops a STALE PV display name — e.g. a pre-rename profile.vendor
        // that PV never updated on rename — from clobbering the correct
        // Shopify name: the collapsed slug of an old name won't match the
        // current collection handle, so we keep Liquid's product.vendor
        // (which Shopify already shows proper-cased everywhere else).
        var nameEl = container.querySelector('[data-seller-name]');
        var collapseName = function (s) {
          return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
        };
        if (
          d.vendorName &&
          nameEl &&
          /[A-Z\s]/.test(d.vendorName) &&
          collapseName(d.vendorName) === collapseName(handle)
        ) {
          nameEl.textContent = d.vendorName;
        }

        // Description — the bio source is `description` from the rankings
        // worker's /vendor response (portal-managed About Me upstream).
        // CORRECTED 2026-06-14: the prior [data-about-locked] /
        // tbb.about_me priority was wrong — that metafield is the
        // "About My Top 8" blurb, a separate portal field. The lock
        // attribute is no longer emitted by the section; the check stays
        // as a safety valve in case markup regresses.
        if (d.description) {
          var descEl = container.querySelector('[data-seller-description]');
          if (descEl && !descEl.hasAttribute('data-about-locked')) descEl.textContent = d.description;
        }

        // Location — defensive: handle string, object, or empty.
        // Worker normalizes to a display string, but we guard against
        // legacy object shapes so a stale CF edge cache can never render
        // "[object Object]". Cascade matches the worker's logic so the
        // fallback path produces equivalent output for international
        // sellers (Canadians, UK, etc.) without state-equivalent data.
        var locStr = '';
        if (typeof d.location === 'string') {
          locStr = sanitizeLocation(d.location);
        } else if (d.location && typeof d.location === 'object') {
          var city = (d.location.city || '').trim();
          var state = (d.location.state || '').trim();
          var country = (d.location.country || '').trim();
          if (city && state) locStr = city + ', ' + state;
          else if (city && country) locStr = city + ', ' + country;
          else locStr = city || state || country || '';
        }
        if (locStr) {
          var locText = container.querySelector('[data-seller-location-text]');
          if (locText) locText.textContent = locStr;
          var locWrap = container.querySelector('[data-seller-location]');
          if (locWrap) locWrap.style.display = '';
        }

        // Stats
        var prodEl = container.querySelector('[data-seller-stat-products]');
        if (prodEl) prodEl.textContent = formatCount(d.productsCount || 0);

        var salesEl = container.querySelector('[data-seller-stat-sales]');
        if (salesEl) salesEl.textContent = formatCount(d.totalOrders || 0);

        // Last re-up date — worker pre-formats as "M/D/YY". Reveal
        // only when non-empty (sellers with no products yet → hidden).
        if (d.lastReupAt) {
          var reupEl = container.querySelector('[data-seller-reup]');
          var reupDate = container.querySelector('[data-seller-reup-date]');
          if (reupEl && reupDate) {
            reupDate.textContent = d.lastReupAt;
            reupEl.removeAttribute('hidden');
          }
        }

        // Contact icons — each chip stays hidden unless safeUrl returns
        // a non-empty href. Row collapses gracefully around populated
        // channels (works for any 0–7 combination).
        var social = d.social || {};
        setContactChip(container, 'data-seller-contact-phone',     safeUrl(d.phone, 'tel'));
        setContactChip(container, 'data-seller-contact-website',   safeUrl(d.website, 'web'));
        setContactChip(container, 'data-seller-contact-email',     safeUrl(social.email, 'mailto'));
        setContactChip(container, 'data-seller-contact-instagram', safeUrl(social.instagram, 'web'));
        setContactChip(container, 'data-seller-contact-facebook',  safeUrl(social.facebook, 'web'));
        setContactChip(container, 'data-seller-contact-youtube',   safeUrl(social.youtube, 'web'));
        setContactChip(container, 'data-seller-contact-whatsapp',  safeUrl(social.whatsapp, 'web'));
        setContactChip(container, 'data-seller-contact-tiktok',    safeUrl(social.tiktok, 'web'));

        // Vacation banner — PV is canonical now (worker's data.vacation).
        // Render only when isActive === true; otherwise the object may
        // still carry stale `message` text from the seller's last vacation.
        // Banner replaces the status pill visually (mutually exclusive).
        if (d.vacation && d.vacation.isActive === true) {
          var vacBanner = container.querySelector('[data-tbb-vacation-banner]');
          var pillForHide = container.querySelector('[data-tbb-status-pill]');
          // Translate worker shape → renderVacationBanner's expected shape.
          // Worker pre-formatted returnsAt as "MMM D" so we hand it
          // straight into the existing fallback message logic.
          renderVacationBanner(vacBanner, {
            active: true,
            message: d.vacation.message,
            returnsAt: d.vacation.returnsAt
          });
          if (pillForHide) pillForHide.setAttribute('hidden', '');
        }

        // (Message mailto button removed 2026-05-07 — no real messaging
        // workflow yet. Email contact chip in the icon row covers the
        // "I want to email this seller" path until we ship a proper
        // buyer↔seller messaging surface.)

        // Mark as hydrated for CSS transitions
        container.classList.add('is-hydrated');
      })
      .catch(function (err) {
        console.warn('TBB seller profile fetch failed:', err.message);
        // Liquid fallbacks remain visible — still mark hydrated so the
        // vinyl loading overlay doesn't hang forever on timeout/error.
        container.classList.add('is-hydrated');
      });
  }

  function initAll() {
    document.querySelectorAll('[data-tbb-seller-header]').forEach(initSellerHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor live preview
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var header = e.target.querySelector('[data-tbb-seller-header]');
      if (header) {
        header.removeAttribute('data-tbb-seller-init');
        initSellerHeader(header);
      }
    });
  }
})();
